import React from 'react';
import { motion } from 'framer-motion';

const LoadingSpinner = ({ size = 'default', color = 'purple', text = '', fullScreen = false }) => {
  const sizeConfig = {
    small: { spinner: 'w-5 h-5', container: 'gap-2', text: 'text-xs' },
    default: { spinner: 'w-10 h-10', container: 'gap-3', text: 'text-sm' },
    large: { spinner: 'w-16 h-16', container: 'gap-4', text: 'text-base' },
    xl: { spinner: 'w-24 h-24', container: 'gap-5', text: 'text-lg' }
  };

  const colorConfig = {
    purple: { primary: 'from-purple-500 to-pink-500', secondary: 'border-purple-500/20', glow: 'shadow-purple-500/25' },
    blue: { primary: 'from-blue-500 to-cyan-500', secondary: 'border-blue-500/20', glow: 'shadow-blue-500/25' },
    green: { primary: 'from-emerald-500 to-teal-500', secondary: 'border-emerald-500/20', glow: 'shadow-emerald-500/25' },
    red: { primary: 'from-red-500 to-pink-500', secondary: 'border-red-500/20', glow: 'shadow-red-500/25' },
    amber: { primary: 'from-amber-500 to-orange-500', secondary: 'border-amber-500/20', glow: 'shadow-amber-500/25' },
    white: { primary: 'from-white to-gray-200', secondary: 'border-white/20', glow: 'shadow-white/25' }
  };

  const config = sizeConfig[size] || sizeConfig.default;
  const colors = colorConfig[color] || colorConfig.purple;

  const SpinnerContent = () => (
    <div className={`flex flex-col items-center justify-center ${config.container}`}>
      {/* Spinner Container */}
      <div className="relative">
        {/* Outer Ring */}
        <motion.div
          className={`${config.spinner} rounded-full border-4 ${colors.secondary}`}
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Inner Spinning Gradient */}
        <motion.div
          className={`absolute inset-0 ${config.spinner} rounded-full`}
          style={{
            background: `conic-gradient(from 0deg, transparent, transparent 70%, var(--tw-gradient-from), var(--tw-gradient-to))`,
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <div className={`w-full h-full rounded-full bg-gradient-to-r ${colors.primary} opacity-80`} 
               style={{ 
                 maskImage: 'conic-gradient(from 0deg, transparent 0%, black 25%, black 50%, transparent 75%)',
                 WebkitMaskImage: 'conic-gradient(from 0deg, transparent 0%, black 25%, black 50%, transparent 75%)'
               }} 
          />
        </motion.div>

        {/* Center Dot */}
        <motion.div
          className={`absolute inset-0 flex items-center justify-center`}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${colors.primary} shadow-lg ${colors.glow}`} />
        </motion.div>

        {/* Orbiting Dots */}
        {size !== 'small' && (
          <>
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <div className={`absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gradient-to-r ${colors.primary}`} />
            </motion.div>
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: -360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <div className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-gradient-to-r ${colors.primary} opacity-60`} />
            </motion.div>
          </>
        )}
      </div>

      {/* Loading Text */}
      {text && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-1"
        >
          <span className={`${config.text} font-medium text-gray-400`}>{text}</span>
          <motion.span
            className={`${config.text} text-gray-400`}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            ...
          </motion.span>
        </motion.div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm">
        <SpinnerContent />
      </div>
    );
  }

  return <SpinnerContent />;
};

export default LoadingSpinner;
